import { apiService } from './api'
import { Team, TeamMember, TeamMemberResponse, TeamInvitation, PersonalWorkspace, TeamSearchParams, TeamFilters, TeamStats, CreateTeamData } from '../types/teams'

export const teamsService = {
  // Team Management
  async getUserTeams(includePersonal: boolean = false): Promise<Team[]> {
    const params = includePersonal ? { includePersonal: 'true' } : {};
    const response = await apiService.get('/teams', { params })
    return response.data.data || response.data
  },

  async getTeamDetails(teamId: string): Promise<Team> {
    const response = await apiService.get(`/teams/${teamId}`)
    return response.data.data || response.data
  },

  async createTeam(teamData: CreateTeamData): Promise<Team> {
    const response = await apiService.post('/teams', teamData)
    return response.data.data || response.data
  },

  async updateTeam(teamId: string, updates: Partial<Team>): Promise<Team> {
    const response = await apiService.put(`/teams/${teamId}`, updates)
    return response.data.data || response.data
  },

  async deleteTeam(teamId: string): Promise<void> {
    await apiService.delete(`/teams/${teamId}`)
  },

  async leaveTeam(teamId: string): Promise<void> {
    await apiService.post(`/teams/${teamId}/leave`)
  },

  // Member Management
  async getTeamMembers(teamId: string): Promise<TeamMemberResponse[]> {
    const response = await apiService.get(`/teams/${teamId}/members`)
    return response.data.data || response.data
  },

  async addMember(teamId: string, userData: { email?: string; username?: string; user_id?: string }, role: string = 'developer'): Promise<TeamMemberResponse> {
    const response = await apiService.post(`/teams/${teamId}/members`, { user_data: userData, role })
    return response.data.data || response.data
  },

  async updateMemberRole(teamId: string, userId: string, role: string): Promise<TeamMemberResponse> {
    const response = await apiService.put(`/teams/${teamId}/members/${userId}`, { role })
    return response.data.data || response.data
  },

  async removeMember(teamId: string, userId: string): Promise<void> {
    await apiService.delete(`/teams/${teamId}/members/${userId}`)
  },

  // Legacy invitation methods (for backwards compatibility)
  async inviteMembers(teamId: string, emails: string[], role: string = 'member'): Promise<void> {
    await apiService.post(`/teams/${teamId}/invitations`, { emails, role })
  },

  // Invitation Management
  async getInvitations(): Promise<TeamInvitation[]> {
    const response = await apiService.get('/teams/invitations')
    return response.data.data || response.data
  },

  async acceptInvitation(invitationId: string): Promise<void> {
    await apiService.post(`/teams/invitations/${invitationId}/accept`)
  },

  async declineInvitation(invitationId: string): Promise<void> {
    await apiService.post(`/teams/invitations/${invitationId}/decline`)
  },

  async cancelInvitation(invitationId: string): Promise<void> {
    await apiService.delete(`/teams/invitations/${invitationId}`)
  },

  // Personal Workspace
  async getPersonalWorkspace(): Promise<PersonalWorkspace> {
    const response = await apiService.get('/teams/personal')
    return response.data.data || response.data
  },

  async updatePersonalWorkspace(updates: Partial<PersonalWorkspace>): Promise<PersonalWorkspace> {
    const response = await apiService.put('/teams/personal', updates)
    return response.data.data || response.data
  },

  // Search & Discovery
  async searchTeams(params: TeamSearchParams): Promise<{
    teams: Team[]
    total: number
    page: number
    limit: number
  }> {
    const response = await apiService.get('/teams/search', { params })
    return response.data.data || response.data
  },

  async getPublicTeams(filters?: TeamFilters): Promise<Team[]> {
    const response = await apiService.get('/teams/public', { params: filters })
    return response.data.data || response.data
  },

  async joinTeam(teamId: string): Promise<void> {
    await apiService.post(`/teams/${teamId}/join`)
  },

  // Team Statistics
  async getTeamStats(teamId: string): Promise<TeamStats> {
    const response = await apiService.get(`/teams/${teamId}/stats`)
    return response.data.data || response.data
  },

  async getTeamActivity(teamId: string, limit?: number): Promise<any[]> {
    const response = await apiService.get(`/teams/${teamId}/activity`, { params: { limit } })
    return response.data.data || response.data
  },

  // Avatar Management
  async uploadTeamAvatar(teamId: string, file: File): Promise<string> {
    const formData = new FormData()
    formData.append('avatar', file)
    
    const response = await apiService.post(`/teams/${teamId}/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    return response.data.avatarUrl
  },

  async deleteTeamAvatar(teamId: string): Promise<void> {
    await apiService.delete(`/teams/${teamId}/avatar`)
  },

  // Project Management within Teams
  async getTeamProjects(teamId: string): Promise<any[]> {
    const response = await apiService.get(`/teams/${teamId}/projects`)
    return response.data
  },

  async addProjectToTeam(teamId: string, projectId: string): Promise<void> {
    await apiService.post(`/teams/${teamId}/projects`, { projectId })
  },

  async removeProjectFromTeam(teamId: string, projectId: string): Promise<void> {
    await apiService.delete(`/teams/${teamId}/projects/${projectId}`)
  },

  // Team Settings
  async getTeamSettings(teamId: string): Promise<any> {
    const response = await apiService.get(`/teams/${teamId}/settings`)
    return response.data
  },

  async updateTeamSettings(teamId: string, settings: any): Promise<void> {
    await apiService.put(`/teams/${teamId}/settings`, settings)
  },

  // Permissions
  async getTeamPermissions(teamId: string): Promise<any> {
    const response = await apiService.get(`/teams/${teamId}/permissions`)
    return response.data
  },

  async updateTeamPermissions(teamId: string, permissions: any): Promise<void> {
    await apiService.put(`/teams/${teamId}/permissions`, permissions)
  },

  // Utility Functions
  async checkTeamSlug(slug: string): Promise<boolean> {
    const response = await apiService.get(`/teams/check-slug/${slug}`)
    return response.data.available
  },

  async generateTeamInviteLink(teamId: string, expiresIn?: number): Promise<string> {
    const response = await apiService.post(`/teams/${teamId}/invite-link`, { expiresIn })
    return response.data.inviteLink
  },

  async joinTeamByInviteLink(inviteCode: string): Promise<void> {
    await apiService.post(`/teams/join-by-invite/${inviteCode}`)
  },

  // Analytics
  async getTeamAnalytics(teamId: string, period: 'week' | 'month' | 'year'): Promise<any> {
    const response = await apiService.get(`/teams/${teamId}/analytics`, { params: { period } })
    return response.data
  },

  async getPersonalAnalytics(): Promise<any> {
    const response = await apiService.get('/teams/personal-analytics')
    return response.data
  }
} 